import { flushSync } from 'react-dom';
import { useAuthStore } from '@/stores/authStore';
import { approvePageUnload, withdrawPageUnloadApproval } from '@/utils/unsavedChanges';

let exiting: Promise<void> | null = null;

/**
 * The tab is about to leave its team: a team switch, a renewal that came back on another team, or
 * a sign-out in another tab. Swaps the team pages for a full-page `label` (app.tsx), so they
 * unmount while this tab still holds its current session: their last saves, guards and live
 * connection end with the old team's token, and nothing writes the old team's state under the new
 * team's keys.
 *
 * Call it only right before storing a new session or a full-page navigation: it lets the page
 * unload without asking, until the page reloads. Resolves once the team pages have unmounted and
 * their clean-ups have run; a second call waits for the first. Doesn't import lib/api, which
 * imports this file.
 */
export function beginTeamExit(label: string): Promise<void> {
  exiting ??= (async () => {
    approvePageUnload();
    flushSync(() => useAuthStore.getState().setTeamExit({ label }));
    // flushSync commits the unmount; one macrotask lets any passive clean-ups still queued run.
    await new Promise((resolve) => setTimeout(resolve, 0));
  })();
  return exiting;
}

/**
 * The exit didn't happen after all (a sign-out the server didn't confirm): the guards ask again,
 * the team pages may show, and a later exit starts afresh.
 */
export function cancelTeamExit(): void {
  exiting = null;
  withdrawPageUnloadApproval();
  useAuthStore.getState().setTeamExit(null);
}
